import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Trophy, Medal, Crown, Star, Shield } from "lucide-react";

const TIER_STYLES = {
  bronze: { bg: "from-orange-700 via-orange-600 to-orange-800", glow: "shadow-orange-600/40", text: "text-orange-200", ring: "border-orange-500/50" },
  silver: { bg: "from-slate-400 via-slate-300 to-slate-500", glow: "shadow-slate-400/40", text: "text-slate-100", ring: "border-slate-300/50" },
  gold: { bg: "from-yellow-500 via-amber-400 to-yellow-600", glow: "shadow-yellow-500/50", text: "text-yellow-50", ring: "border-yellow-400/60" },
  platinum: { bg: "from-cyan-400 via-blue-300 to-indigo-500", glow: "shadow-cyan-400/50", text: "text-cyan-50", ring: "border-cyan-400/60" },
  diamond: { bg: "from-fuchsia-400 via-purple-400 to-cyan-300", glow: "shadow-fuchsia-400/60", text: "text-white", ring: "border-fuchsia-300/70" },
};

const ICON_MAP = { trophy: Trophy, medal: Medal, crown: Crown, star: Star, shield: Shield };

export function Badge({ ach, awarded_at, context, size = "md" }) {
  const tier = TIER_STYLES[ach.tier] || TIER_STYLES.gold;
  const Icon = ICON_MAP[ach.icon] || Trophy;
  const sizing = size === "sm" ? "w-20 h-20" : size === "lg" ? "w-40 h-40" : "w-28 h-28";
  const iconSize = size === "sm" ? "w-7 h-7" : size === "lg" ? "w-14 h-14" : "w-10 h-10";

  return (
    <div className="flex flex-col items-center text-center">
      <div className={`relative ${sizing}`} data-testid={`badge-${ach.id}`}>
        {/* Outer ring */}
        <div className={`absolute inset-0 rounded-full border-4 ${tier.ring} ${tier.glow} shadow-2xl`} />
        {/* Gradient body */}
        <div className={`absolute inset-2 rounded-full bg-gradient-to-br ${tier.bg} flex items-center justify-center`}>
          <div className="absolute inset-0 rounded-full bg-black/20" />
          <Icon className={`relative ${iconSize} ${tier.text}`} strokeWidth={1.5} />
        </div>
        {/* Sparkle */}
        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white/80 blur-sm animate-pulse" />
      </div>
      <div className={`mt-3 font-display ${size === "sm" ? "text-xs" : "text-base"} tracking-tight`}>{ach.title}</div>
      <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/40 mt-0.5">
        {ach.tier} · {ach.season || "—"}
      </div>
      {context && <div className="font-mono text-[10px] text-white/50 mt-1 italic">{context}</div>}
      {awarded_at && (
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/30 mt-1">
          {new Date(awarded_at).toLocaleDateString("de-DE")}
        </div>
      )}
    </div>
  );
}

export default function Achievements() {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [tab, setTab] = useState("mine");

  useEffect(() => {
    if (!user) return;
    api.get(`/users/${user.user_id}/achievements`).then((r) => setMine(r.data));
    api.get("/achievements").then((r) => setAll(r.data));
  }, [user]);

  return (
    <div className="space-y-8 fade-in" data-testid="achievements-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Trophies</div>
        <h1 className="font-display text-4xl tracking-tight mt-2">Deine Erfolge</h1>
        <p className="text-white/60 text-sm mt-2">Medaillen, Pokale und Auszeichnungen.</p>
      </div>

      <div className="flex gap-2">
        {[{k:"mine",l:"Meine Erfolge"},{k:"all",l:"Alle möglichen"}].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`ach-tab-${t.k}`}
            className={`px-4 py-2 rounded-sm text-xs uppercase tracking-[0.2em] font-mono border transition-colors ${
              tab === t.k ? "bg-primary/10 border-primary text-primary" : "border-border text-white/60 hover:border-primary/40"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "mine" ? (
        mine.length === 0 ? (
          <div className="border border-dashed border-white/10 p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-white/40">
            Noch keine Erfolge — gewinne dein erstes Match!
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {mine.map((ua) => ua.achievement && <Badge key={ua.id} ach={ua.achievement} awarded_at={ua.awarded_at} context={ua.context} />)}
          </div>
        )
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {all.map((a) => <Badge key={a.id} ach={a} />)}
        </div>
      )}
    </div>
  );
}
